import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { MAIN_CATEGORY_LABEL, api, apiFile, saveBlob, type Condition, type Location, type MainCategory } from '../api';
import { useAuth } from '../auth';
import { ErrorBox, Field, Loading, Success, fmtDate } from '../components/ui';
import { useConditions, useLocations, useSettings } from '../hooks';

/** Runs an async action with busy/error/success state; ignores repeat presses while busy. */
function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [ok, setOk] = useState<string | null>(null);
  const run = async (fn: () => Promise<unknown>, success?: string) => {
    if (busy) return false;
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      await fn();
      if (success) setOk(success);
      return true;
    } catch (e) {
      setError(e);
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, ok, run };
}

export function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  return (
    <div>
      <h1>{isAdmin ? 'Settings' : 'Account'}</h1>
      <nav className="tabs">
        <NavLink to="/settings/account">My account</NavLink>
        {isAdmin && (
          <>
            <NavLink to="/settings/users">Users</NavLink>
            <NavLink to="/settings/locations">Locations</NavLink>
            <NavLink to="/settings/conditions">Conditions</NavLink>
            <NavLink to="/settings/general">General</NavLink>
            <NavLink to="/settings/retail">Retail pricing</NavLink>
            <NavLink to="/settings/audit">Audit log</NavLink>
          </>
        )}
      </nav>
      <Routes>
        <Route path="account" element={<Account />} />
        {isAdmin && (
          <>
            <Route path="users" element={<Users />} />
            <Route path="locations" element={<Locations />} />
            <Route path="conditions" element={<Conditions />} />
            <Route path="general" element={<General />} />
            <Route path="retail" element={<RetailPricingSettings />} />
            <Route path="audit" element={<Audit />} />
          </>
        )}
        <Route path="*" element={<Navigate to="account" replace />} />
      </Routes>
    </div>
  );
}

function Account() {
  const { user } = useAuth();
  const [f, setF] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const a = useAction();
  // Employees sign in with a 6-digit PIN; administrators with a password.
  const isPin = user?.role !== 'admin';
  const word = isPin ? 'PIN' : 'password';
  const pinProps = isPin ? { inputMode: 'numeric' as const, maxLength: 6, pattern: '\\d{6}', className: 'pin-input' } : {};
  const digits = (v: string) => (isPin ? v.replace(/\D/g, '').slice(0, 6) : v);
  return (
    <form
      className="card narrow"
      onSubmit={async (e) => {
        e.preventDefault();
        if (f.newPassword !== f.confirm) return void a.run(async () => Promise.reject(new Error(`The new ${word}s do not match`)));
        if (await a.run(() => api('/auth/change-password', { body: { currentPassword: f.currentPassword, newPassword: f.newPassword } }), `${isPin ? 'PIN' : 'Password'} changed.`))
          setF({ currentPassword: '', newPassword: '', confirm: '' });
      }}
    >
      <p>
        Signed in as <strong>{user?.displayName}</strong> ({user?.username}, {user?.role}).
      </p>
      <h2>Change {word}</h2>
      <Field label={`Current ${word}`} hint={isPin ? 'If you still have an old password, enter that here' : undefined}>
        <input type="password" autoComplete="current-password" value={f.currentPassword} onChange={(e) => setF({ ...f, currentPassword: e.target.value })} required />
      </Field>
      <Field label={`New ${word}`} hint={isPin ? '6 digits; not 123456 or the same digit six times' : 'At least 10 characters'}>
        <input type="password" autoComplete="new-password" {...pinProps} value={f.newPassword} onChange={(e) => setF({ ...f, newPassword: digits(e.target.value) })} required />
      </Field>
      <Field label={`Repeat new ${word}`}>
        <input type="password" autoComplete="new-password" {...pinProps} value={f.confirm} onChange={(e) => setF({ ...f, confirm: digits(e.target.value) })} required />
      </Field>
      <ErrorBox error={a.error} />
      {a.ok && <Success>{a.ok}</Success>}
      <button className="btn btn-primary" disabled={a.busy}>
        {a.busy ? 'Savingâ€¦' : `Change ${word}`}
      </button>
    </form>
  );
}

interface UserRow {
  id: string;
  username: string;
  displayName: string;
  role: 'admin' | 'employee';
  active: boolean;
  canReceive: boolean;
  createdAt: string;
  secretKind: 'password' | 'pin';
  locked: boolean;
}

function Users() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['users'], queryFn: () => api<{ users: UserRow[] }>('/users').then((r) => r.users) });
  const [f, setF] = useState({ username: '', displayName: '', role: 'employee', password: '' });
  const add = useAction();
  const row = useAction();
  const refresh = () => qc.invalidateQueries({ queryKey: ['users'] });

  return (
    <div>
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error || row.error} />
      {row.ok && <Success>{row.ok}</Success>}
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Name</th>
              <th>Role</th>
              <th>Signs in with</th>
              <th>Status</th>
              <th>Receiving</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {q.data?.map((u) => (
              <tr key={u.id}>
                <td>{u.username}</td>
                <td>{u.displayName}</td>
                <td>
                  <select
                    value={u.role}
                    disabled={row.busy}
                    onChange={(e) => {
                      const role = e.target.value;
                      // An administrator needs a password, not a PIN.
                      let newPassword: string | undefined;
                      if (role === 'admin' && u.secretKind === 'pin') {
                        newPassword = prompt(`${u.username} signs in with a PIN. Set a password for them as an administrator (at least 10 characters):`) ?? undefined;
                        if (!newPassword) return;
                      }
                      row.run(() => api(`/users/${u.id}`, { method: 'PATCH', body: { role, newPassword } }).then(refresh), 'Role updated.');
                    }}
                  >
                    <option value="employee">Employee</option>
                    <option value="admin">Administrator</option>
                  </select>
                </td>
                <td>
                  {u.secretKind === 'pin' ? 'PIN' : u.role === 'admin' ? 'Password' : <span className="muted">Old password: set a PIN</span>}
                </td>
                <td>
                  {u.active ? 'Active' : 'Disabled'}
                  {u.locked && <span className="badge badge-warn">locked</span>}
                </td>
                <td>
                  {u.role === 'admin' ? (
                    <span className="muted small">always</span>
                  ) : (
                    <label className="check">
                      <input
                        type="checkbox"
                        checked={u.canReceive}
                        disabled={row.busy}
                        onChange={(e) =>
                          row.run(
                            () => api(`/users/${u.id}`, { method: 'PATCH', body: { canReceive: e.target.checked } }).then(refresh),
                            e.target.checked ? 'Receiving switched on.' : 'Receiving switched off.',
                          )
                        }
                      />{' '}
                      pilot
                    </label>
                  )}
                </td>
                <td className="row gap">
                  <button
                    className="btn btn-small"
                    disabled={row.busy}
                    onClick={() => row.run(() => api(`/users/${u.id}`, { method: 'PATCH', body: { active: !u.active } }).then(refresh), u.active ? 'User disabled and signed out.' : 'User enabled.')}
                  >
                    {u.active ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    className="btn btn-small"
                    disabled={row.busy}
                    onClick={() => {
                      const pw =
                        u.role === 'admin'
                          ? prompt(`New password for ${u.username} (at least 10 characters):`)
                          : prompt(`New 6-digit PIN for ${u.username} (not 123456 or the same digit six times):`)?.trim();
                      if (pw)
                        row.run(
                          () => api(`/users/${u.id}`, { method: 'PATCH', body: { newPassword: pw } }).then(refresh),
                          `${u.role === 'admin' ? 'Password' : 'PIN'} set. ${u.username} has been signed out and can sign in with it now.`,
                        );
                    }}
                  >
                    {u.role === 'admin' ? 'Reset password' : u.secretKind === 'pin' ? 'Reset PIN' : 'Set PIN'}
                  </button>
                  {u.locked && (
                    <button className="btn btn-small" disabled={row.busy} onClick={() => row.run(() => api(`/users/${u.id}`, { method: 'PATCH', body: { unlock: true } }).then(refresh), `${u.username} unlocked.`)}>
                      Unlock
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <form
        className="card narrow"
        onSubmit={async (e) => {
          e.preventDefault();
          if (await add.run(() => api('/users', { body: f }), `User ${f.username} created.`)) {
            setF({ username: '', displayName: '', role: 'employee', password: '' });
            refresh();
          }
        }}
      >
        <h2>Add user</h2>
        <div className="grid2">
          <Field label="Username">
            <input value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} autoCapitalize="off" required />
          </Field>
          <Field label="Name">
            <input value={f.displayName} onChange={(e) => setF({ ...f, displayName: e.target.value })} required />
          </Field>
          <Field label="Role">
            <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value, password: '' })}>
              <option value="employee">Employee</option>
              <option value="admin">Administrator</option>
            </select>
          </Field>
          {f.role === 'admin' ? (
            <Field label="Initial password" hint="At least 10 characters">
              <input type="password" autoComplete="new-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required />
            </Field>
          ) : (
            <Field label="PIN" hint="6 digits; tell the person their PIN. They can change it under Account.">
              <input
                type="password"
                autoComplete="new-password"
                inputMode="numeric"
                maxLength={6}
                pattern="\d{6}"
                className="pin-input"
                value={f.password}
                onChange={(e) => setF({ ...f, password: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                required
              />
            </Field>
          )}
        </div>
        <ErrorBox error={add.error} />
        {add.ok && <Success>{add.ok}</Success>}
        <button className="btn btn-primary" disabled={add.busy}>
          {add.busy ? 'Savingâ€¦' : 'Add user'}
        </button>
      </form>
    </div>
  );
}

const LOC_FIELDS = ['code', 'warehouse', 'aisle', 'rack', 'shelf', 'bin', 'notes'] as const;
type LocForm = Record<(typeof LOC_FIELDS)[number], string> & { active: boolean };
const emptyLoc: LocForm = { code: '', warehouse: '', aisle: '', rack: '', shelf: '', bin: '', notes: '', active: true };

function Locations() {
  const qc = useQueryClient();
  const q = useLocations(true);
  const [editing, setEditing] = useState<{ id: string | null; f: LocForm } | null>(null);
  const a = useAction();

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    const body = { ...editing.f };
    const ok = await a.run(
      () => (editing.id ? api(`/locations/${editing.id}`, { method: 'PATCH', body }) : api('/locations', { body })),
      editing.id ? 'Location updated.' : `Location ${body.code} added.`,
    );
    if (ok) {
      setEditing(null);
      qc.invalidateQueries({ queryKey: ['locations'] });
    }
  };
  const edit = (l: Location) =>
    setEditing({ id: l.id, f: { code: l.code, warehouse: l.warehouse ?? '', aisle: l.aisle ?? '', rack: l.rack ?? '', shelf: l.shelf ?? '', bin: l.bin ?? '', notes: l.notes ?? '', active: l.active } });
  const printLabels = (ls: Location[]) =>
    a.run(async () => saveBlob(await apiFile('/locations/labels', { locationIds: ls.map((l) => l.id) }), ls.length === 1 ? `location-${ls[0].code}.pdf` : 'location-labels.pdf'),
      `Downloaded ${ls.length} location label(s). Print at actual size on the 1.5â€³ Ã— 1â€³ labels.`);

  return (
    <div>
      <p className="muted">
        Location codes are what staff scan or pick (e.g. a shelf, the packing area, the truck, the store pickup area). Warehouse/aisle/rack/shelf/bin are optional descriptions. Print a
        location's label and stick it on the shelf so staff can scan it. Short codes (up to about 12 characters) print best. Portable bins that travel to the store are managed under{' '}
        <Link to="/bins">Bins</Link>.
      </p>
      {a.ok && <Success>{a.ok}</Success>}
      {editing ? (
        <form className="card narrow" onSubmit={save}>
          <h2>{editing.id ? 'Edit location' : 'Add location'}</h2>
          <div className="grid3">
            {LOC_FIELDS.map((k) => (
              <Field key={k} label={k === 'code' ? 'Code (required)' : k[0].toUpperCase() + k.slice(1)}>
                <input value={editing.f[k]} onChange={(e) => setEditing({ ...editing, f: { ...editing.f, [k]: e.target.value } })} required={k === 'code'} />
              </Field>
            ))}
          </div>
          <label className="check">
            <input type="checkbox" checked={editing.f.active} onChange={(e) => setEditing({ ...editing, f: { ...editing.f, active: e.target.checked } })} /> Active
          </label>
          <ErrorBox error={a.error} />
          <div className="row gap">
            <button className="btn btn-primary" disabled={a.busy}>
              {a.busy ? 'Savingâ€¦' : 'Save'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="row gap">
          <button className="btn btn-primary" onClick={() => setEditing({ id: null, f: emptyLoc })}>
            Add location
          </button>
          {!!q.data?.some((l) => l.active) && (
            <button className="btn" disabled={a.busy} onClick={() => printLabels(q.data!.filter((l) => l.active))}>
              Print labels for all active locations
            </button>
          )}
        </div>
      )}
      {!editing && <ErrorBox error={a.error} />}
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {q.data?.length === 0 && <p className="muted">No locations yet.</p>}
      {!!q.data?.length && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Warehouse</th>
                <th>Aisle</th>
                <th>Rack</th>
                <th>Shelf</th>
                <th>Bin</th>
                <th>Lots</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {q.data.map((l) => (
                <tr key={l.id}>
                  <td className="mono">{l.code}</td>
                  <td>{l.warehouse}</td>
                  <td>{l.aisle}</td>
                  <td>{l.rack}</td>
                  <td>{l.shelf}</td>
                  <td>{l.bin}</td>
                  <td>{l.lotCount}</td>
                  <td>{l.active ? 'Active' : 'Inactive'}</td>
                  <td>
                    <button className="btn btn-small" onClick={() => edit(l)}>
                      Edit
                    </button>{' '}
                    <button className="btn btn-small btn-ghost" disabled={a.busy} onClick={() => printLabels([l])}>
                      Label
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const STANDARD_CONDITIONS = ['New', 'Like New', 'Used', 'AS-IS'];

function Conditions() {
  const qc = useQueryClient();
  const q = useConditions();
  const [label, setLabel] = useState('');
  const a = useAction();
  const refresh = () => qc.invalidateQueries({ queryKey: ['conditions'] });
  const patch = (c: Condition, body: Partial<Condition>) => a.run(() => api(`/conditions/${c.id}`, { method: 'PATCH', body }).then(refresh));
  // Renumber 10, 20, 30â€¦ with item i swapped above item i-1.
  const moveUp = (i: number) => {
    const list = [...(q.data ?? [])];
    [list[i - 1], list[i]] = [list[i], list[i - 1]];
    a.run(async () => {
      for (const [idx, c] of list.entries()) {
        if (c.sortOrder !== (idx + 1) * 10) await api(`/conditions/${c.id}`, { method: 'PATCH', body: { sortOrder: (idx + 1) * 10 } });
      }
      await refresh();
    });
  };

  return (
    <div className="narrow">
      <p className="muted">These are the choices staff pick from when creating a lot. Renaming or disabling a choice does not change lots that were already saved.</p>
      <ErrorBox error={a.error || q.error} />
      {a.ok && <Success>{a.ok}</Success>}
      <ul className="plain">
        {q.data?.map((c, i) => (
          <li key={c.id} className="row gap">
            <span className={c.active ? '' : 'muted'}>{c.label}</span>
            {!c.active && <span className="badge">inactive</span>}
            <button className="btn btn-small btn-ghost" disabled={a.busy || i === 0} onClick={() => moveUp(i)} title="Move up">
              â–²
            </button>
            <button
              className="btn btn-small btn-ghost"
              onClick={() => {
                const v = prompt('Rename condition', c.label);
                if (v && v.trim() !== c.label) patch(c, { label: v.trim() });
              }}
            >
              Rename
            </button>
            <button className="btn btn-small btn-ghost" disabled={a.busy} onClick={() => patch(c, { active: !c.active })}>
              {c.active ? 'Disable' : 'Enable'}
            </button>
          </li>
        ))}
      </ul>
      {q.data?.length === 0 && (
        <div className="alert alert-info">
          No conditions yet.{' '}
          <button
            className="btn btn-small btn-primary"
            disabled={a.busy}
            onClick={() =>
              a.run(async () => {
                for (const [i, label] of STANDARD_CONDITIONS.entries()) await api('/conditions', { body: { label, sortOrder: (i + 1) * 10 } });
                await refresh();
              }, 'Added New, Like New, Used and AS-IS.')
            }
          >
            Add New, Like New, Used, AS-IS
          </button>
        </div>
      )}
      <form
        className="row gap"
        onSubmit={async (e) => {
          e.preventDefault();
          const next = (q.data?.reduce((m, c) => Math.max(m, c.sortOrder), 0) ?? 0) + 10;
          if (await a.run(() => api('/conditions', { body: { label, sortOrder: next } }).then(refresh), `Added "${label}".`)) setLabel('');
        }}
      >
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="New condition label" required />
        <button className="btn btn-primary" disabled={a.busy}>
          Add
        </button>
      </form>
    </div>
  );
}

function General() {
  const qc = useQueryClient();
  const s = useSettings();
  const sys = useQuery({ queryKey: ['system'], queryFn: () => api<{ environment: string; photoStorage: string; migrations: { name: string; appliedAt: string }[] }>('/system') });
  const [cur, setCur] = useState('');
  const a = useAction();
  return (
    <div className="narrow">
      <form
        className="card"
        onSubmit={async (e) => {
          e.preventDefault();
          if (await a.run(() => api('/settings', { method: 'PUT', body: { defaultCurrency: cur || s.data?.defaultCurrency } }), 'Saved.')) qc.invalidateQueries({ queryKey: ['settings'] });
        }}
      >
        <h2>Default currency</h2>
        <Field label="Used as the suggested currency on forms and imports" hint="3-letter code, e.g. CAD">
          <input value={cur || s.data?.defaultCurrency || ''} onChange={(e) => setCur(e.target.value.toUpperCase())} maxLength={3} />
        </Field>
        <ErrorBox error={a.error} />
        {a.ok && <Success>{a.ok}</Success>}
        <button className="btn btn-primary" disabled={a.busy}>
          Save
        </button>
      </form>
      <section className="card">
        <h2>Lot labels</h2>
        <label className="check">
          <input
            type="checkbox"
            checked={!!s.data?.allowUnissuedLabels}
            disabled={a.busy || !s.data}
            onChange={(e) =>
              a.run(() => api('/settings', { method: 'PUT', body: { allowUnissuedLabels: e.target.checked } }).then(() => qc.invalidateQueries({ queryKey: ['settings'] })), 'Saved.')
            }
          />
          Accept lot label numbers that were NOT issued by this system
        </label>
        <p className="small muted">Leave off unless you use labels printed elsewhere. When off, scanning a product barcode into the lot-label box is refused.</p>
      </section>
      {s.data && <CostPercentCard current={s.data.costPercent} />}
      <section className="card">
        <h2>System</h2>
        {sys.isLoading && <Loading />}
        <ErrorBox error={sys.error} />
        {sys.data && (
          <dl className="dl">
            <dt>Environment</dt>
            <dd>
              <strong>{sys.data.environment}</strong>
            </dd>
            <dt>Photo storage</dt>
            <dd>{sys.data.photoStorage}</dd>
            <dt>Database migrations</dt>
            <dd>
              {sys.data.migrations.map((m) => (
                <div key={m.name} className="small">
                  {m.name} <span className="muted">({fmtDate(m.appliedAt)})</span>
                </div>
              ))}
            </dd>
          </dl>
        )}
      </section>
    </div>
  );
}

interface AuditEntry {
  id: string;
  occurredAt: string;
  action: string;
  entityType: string;
  entityId: string | null;
  details: unknown;
  userName: string | null;
}

function Audit() {
  const [before, setBefore] = useState<string | null>(null);
  const [type, setType] = useState('');
  const q = useQuery({
    queryKey: ['audit', before, type],
    queryFn: () => api<{ entries: AuditEntry[] }>(`/audit?${new URLSearchParams({ ...(before ? { before } : {}), ...(type ? { entityType: type } : {}) })}`).then((r) => r.entries),
  });
  return (
    <div>
      <div className="filters">
        <select value={type} onChange={(e) => (setType(e.target.value), setBefore(null))} aria-label="Type">
          <option value="">All records</option>
          {['lot', 'bin', 'product', 'product_import', 'location', 'condition', 'user', 'settings'].map((t) => (
            <option key={t}>{t}</option>
          ))}
        </select>
      </div>
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      <div className="table-wrap">
        <table className="table small">
          <thead>
            <tr>
              <th>When</th>
              <th>Who</th>
              <th>Action</th>
              <th>Record</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {q.data?.map((e) => (
              <tr key={e.id}>
                <td>{fmtDate(e.occurredAt)}</td>
                <td>{e.userName ?? 'system'}</td>
                <td>{e.action}</td>
                <td>
                  {e.entityType} {e.entityId}
                </td>
                <td className="pre audit-details">{JSON.stringify(e.details)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {q.data && q.data.length === 100 && (
        <button className="btn" onClick={() => setBefore(q.data![q.data!.length - 1].id)}>
          Older entries
        </button>
      )}
      {before && (
        <button className="btn btn-ghost" onClick={() => setBefore(null)}>
          Back to newest
        </button>
      )}
    </div>
  );
}

interface RetailSettings {
  settings: { defaultPercent: number; categories: Record<string, number> };
  categories: { category: string; products: number }[];
}
interface PercentPreview {
  count: number;
  items: { productId: string; packSize: number | null; itemNumber: string; title: string | null; from: string; to: string }[];
}

/** Retail price %: a default and optional per-category %, changed with a preview of what it does. */
function RetailPricingSettings() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['retail-settings'], queryFn: () => api<RetailSettings>('/retail/settings') });
  const [category, setCategory] = useState('');
  const [percent, setPercent] = useState('');
  const [preview, setPreview] = useState<PercentPreview | null>(null);
  const a = useAction();
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  const { settings, categories } = q.data!;
  const cat = category === '' ? null : category;
  const current = cat === null ? settings.defaultPercent : (settings.categories[cat] ?? settings.defaultPercent);
  const pct = Number(percent);
  const valid = pct > 0 && pct <= 200;

  const save = (scope: 'new_only' | 'reprice') =>
    a.run(async () => {
      const r = await api<{ repriced: number }>('/retail/percent', { body: { category: cat, percent: pct, scope } });
      setPreview(null);
      setPercent('');
      await qc.invalidateQueries({ queryKey: ['retail-settings'] });
      await qc.invalidateQueries({ queryKey: ['retail'] });
      return r;
    }, scope === 'reprice' ? 'Saved, and the affected prices were changed.' : 'Saved. Existing prices are unchanged; new prices use the new %.');

  return (
    <div className="card">
      <h2>Retail pricing</h2>
      <p className="small">
        Retail price = a % of Costco retail, rounded up to end in .99. A price is set once per item and stays until someone changes it.
      </p>
      <dl className="dl">
        <dt>Default</dt>
        <dd>{settings.defaultPercent}%</dd>
        {Object.entries(settings.categories).map(([c, p]) => (
          <div key={c} style={{ display: 'contents' }}>
            <dt>{c}</dt>
            <dd>{p}%</dd>
          </div>
        ))}
      </dl>
      <h3>Change a %</h3>
      <div className="grid2">
        <Field label="For">
          <select value={category} onChange={(e) => (setCategory(e.target.value), setPreview(null))}>
            <option value="">Default (categories without their own %)</option>
            {categories.map((c) => (
              <option key={c.category} value={c.category}>
                {c.category} ({c.products} items)
              </option>
            ))}
          </select>
        </Field>
        <Field label={`New % (now ${current}%)`}>
          <input value={percent} onChange={(e) => (setPercent(e.target.value), setPreview(null))} inputMode="decimal" placeholder="e.g. 65" />
        </Field>
      </div>
      <button
        className="btn"
        disabled={!valid || a.busy}
        onClick={() => a.run(async () => setPreview(await api<PercentPreview>('/retail/percent/preview', { body: { category: cat, percent: pct } })))}
      >
        Preview
      </button>
      {preview && (
        <div className="mt">
          <p>
            <strong>{preview.count}</strong> item{preview.count === 1 ? '' : 's'} priced by the rule would change. Prices typed by staff are never
            changed.
          </p>
          {preview.items.length > 0 && (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Item #</th>
                    <th>Title</th>
                    <th>Now</th>
                    <th>Would be</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.items.slice(0, 50).map((i) => (
                    <tr key={`${i.productId}-${i.packSize ?? 0}`}>
                      <td className="mono">
                        {i.itemNumber}
                        {i.packSize ? `-${i.packSize}` : ''}
                      </td>
                      <td>{i.title}</td>
                      <td>${i.from}</td>
                      <td>${i.to}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="row gap mt">
            <button className="btn" disabled={a.busy} onClick={() => save('new_only')}>
              Save: new items only
            </button>
            {preview.count > 0 && (
              <button className="btn btn-primary" disabled={a.busy} onClick={() => confirm(`Change ${preview.count} prices now?`) && save('reprice')}>
                Save and re-price {preview.count} item{preview.count === 1 ? '' : 's'}
              </button>
            )}
          </div>
          <p className="muted small">Re-priced items need new labels; this is listed per store once store stock is tracked.</p>
        </div>
      )}
      {a.ok && <Success>{a.ok}</Success>}
      <ErrorBox error={a.error} />
    </div>
  );
}

/** What we pay as a % of Costco retail, per main category: used to estimate lot costs. */
function CostPercentCard({ current }: { current: Record<MainCategory, number> }) {
  const qc = useQueryClient();
  const a = useAction();
  const [v, setV] = useState({ hard_goods: String(current.hard_goods), clothing: String(current.clothing) });
  return (
    <form
      className="card"
      onSubmit={async (e) => {
        e.preventDefault();
        const body = { costPercent: { hard_goods: Number(v.hard_goods), clothing: Number(v.clothing) } };
        if (await a.run(() => api('/settings', { method: 'PUT', body }), 'Saved. New and edited lots use these.')) qc.invalidateQueries({ queryKey: ['settings'] });
      }}
    >
      <h2>Cost (% of retail)</h2>
      <p className="small muted">
        What we normally pay, as a % of Costco retail. A lot with no manifest cost gets an estimated cost of retail x this %. Lots already saved keep theirs.
      </p>
      <div className="grid2">
        {(['hard_goods', 'clothing'] as const).map((k) => (
          <Field key={k} label={`${MAIN_CATEGORY_LABEL[k]} %`}>
            <input value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} inputMode="decimal" />
          </Field>
        ))}
      </div>
      <ErrorBox error={a.error} />
      {a.ok && <Success>{a.ok}</Success>}
      <button className="btn btn-primary" disabled={a.busy}>
        Save
      </button>
    </form>
  );
}
